import { Link } from "react-router-dom";
import { useReveal } from "../../hooks/useReveal";

interface MidPageCTAProps {
  headline?: string;
  subtext?: string;
  ctaLabel?: string;
  ctaHref?: string;
  secondaryLabel?: string;
  secondaryHref?: string;
}

export default function MidPageCTA({
  headline = "Your Family's Story Deserves to Live Forever",
  subtext = "From the first breath to the walk down the aisle — Stephanie captures every chapter with the care it deserves.",
  ctaLabel = "Create Your Custom Keepsake",
  ctaHref = "/services",
  secondaryLabel = "See Real Client Stories",
  secondaryHref = "/about",
}: MidPageCTAProps) {
  const ref = useReveal<HTMLElement>();

  return (
    <section ref={ref} className="relative overflow-hidden py-0">

      {/* ── Full-bleed editorial image strip ─────────────────── */}
      <div className="relative w-full" style={{ height: "520px" }}>
        <img
          src="https://public.readdy.ai/ai/img_res/26a92529a35252be1a5e375048efafa8.jpg"
          alt="Family keepsake photography — Precious Keepsakes San Antonio"
          className="w-full h-full object-cover object-center"
        />
        {/* Overlay — left heavy to give text room */}
        <div className="absolute inset-0 bg-gradient-to-r from-pk-dark/92 via-pk-dark/65 to-pk-dark/10" />
        <div className="absolute inset-0 bg-gradient-to-t from-pk-dark/40 via-transparent to-pk-dark/25" />

        {/* Content */}
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-screen-xl mx-auto px-8 w-full">
            <div className="max-w-xl">

              {/* Eyebrow */}
              <div className="reveal flex items-center gap-3 mb-5">
                <div className="w-8 h-px bg-pk-gold" />
                <span className="font-sans text-[10px] tracking-widest3 uppercase text-pk-gold/90">
                  Begin Your Journey
                </span>
              </div>

              <h2 className="reveal font-display font-light text-white leading-tight" style={{ fontSize: "clamp(2rem, 4vw, 3.6rem)" }}>
                {headline}
              </h2>

              <p className="reveal delay-200 font-sans text-sm text-white/60 mt-5 leading-7 max-w-sm">
                {subtext}
              </p>

              <div className="reveal delay-300 mt-9 flex flex-col sm:flex-row items-start gap-4">
                <Link
                  to={ctaHref}
                  className="bg-pk-gold text-white font-sans text-xs tracking-widest3 uppercase px-12 py-4 rounded-sm hover:bg-white hover:text-pk-charcoal transition-colors duration-400 whitespace-nowrap cursor-pointer"
                >
                  {ctaLabel}
                </Link>
                <Link
                  to={secondaryHref}
                  className="flex items-center gap-2 font-sans text-xs tracking-widest2 uppercase text-white/50 hover:text-pk-gold transition-colors duration-300 whitespace-nowrap cursor-pointer py-3"
                >
                  {secondaryLabel}
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>

              {/* Proof strip */}
              <div className="reveal delay-400 mt-7 flex items-center gap-2">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-star-fill text-pk-gold" style={{ fontSize: "12px" }} />
                  </div>
                ))}
                <span className="font-sans text-xs text-white/40 ml-1">5.0 rating · 500+ San Antonio families</span>
              </div>

            </div>
          </div>
        </div>
      </div>

    </section>
  );
}
